import Box from '@mui/material/Box';

import {
  type FeatureKind,
  NEW_LABEL,
  SHOW_NEW_BADGES,
  STATIC_LABEL,
} from '../../features/feature-badges/constants';
import { Badge, type BadgeProps } from './Badge';

export interface FeatureBadgeProps extends Omit<BadgeProps, 'tone' | 'children'> {
  kind: FeatureKind;
}

/**
 * Marks a static concept (sample data, actions do nothing) or a new feature on today's API. The
 * label comes from the feature-badge constants; Static is neutral, New is info.
 */
export function FeatureBadge({ kind, size = 'sm', ...props }: FeatureBadgeProps) {
  if (kind === 'new' && !SHOW_NEW_BADGES) return null;
  return (
    <Badge
      tone={kind === 'new' ? 'info' : 'neutral'}
      size={size}
      data-feature-badge={kind}
      {...props}
    >
      {kind === 'new' ? NEW_LABEL : STATIC_LABEL}
    </Badge>
  );
}

export type StaticBadgeProps = Omit<FeatureBadgeProps, 'kind'>;

/** A part that shows sample data (no API behind it yet). */
export function StaticBadge(props: StaticBadgeProps) {
  return <FeatureBadge kind="static" {...props} />;
}

/** A part that is new in v3 and runs on today's API. */
export function NewBadge(props: StaticBadgeProps) {
  return <FeatureBadge kind="new" {...props} />;
}

/** A table column header that is new in v3: its title and a compact New badge. */
export function NewHeader({ children }: { children: string }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
      {children}
      <NewBadge />
    </Box>
  );
}
